import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from './ui/tooltip';
import { Loader2, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Module-level cache so multiple cells of the same contract reuse one fetch
const sensitiveCache = new Map(); // contractId -> { data, ts }
const inflight = new Map(); // contractId -> Promise

const fetchSensitive = (contractId) => {
  const cached = sensitiveCache.get(contractId);
  if (cached) return Promise.resolve(cached);
  if (inflight.has(contractId)) return inflight.get(contractId);
  const p = axios
    .get(`${API_URL}/contracts/${contractId}/sensitive`)
    .then((res) => {
      sensitiveCache.set(contractId, res.data);
      inflight.delete(contractId);
      return res.data;
    })
    .catch((err) => {
      inflight.delete(contractId);
      throw err;
    });
  inflight.set(contractId, p);
  return p;
};

const fmtTelefones = (telefones) => {
  if (!Array.isArray(telefones) || telefones.length === 0) return '-';
  return telefones.map((t) => t.numero).filter(Boolean).join(' / ');
};

const fmtEmails = (emails) => {
  if (!Array.isArray(emails) || emails.length === 0) return '-';
  return emails.filter(Boolean).join(' / ');
};

const getRawValue = (data, field) => {
  if (!data) return null;
  if (field === 'telefones' || field === 'telefone') return fmtTelefones(data.telefones);
  if (field === 'emails' || field === 'email') return fmtEmails(data.emails);
  const v = data[field];
  if (v === null || v === undefined || v === '') return '-';
  return String(v);
};

/**
 * Hover-to-reveal wrapper for RGPD-masked fields.
 * Renders the masked value and on hover fetches the raw value
 * (only allowed if the user can already view that contract).
 *
 * Props:
 *   contractId - the contract ID
 *   field      - one of: nif, cpe, cui, iban, morada, localidade, codigo_postal,
 *                       email/emails, telefone/telefones, nome_cliente
 *   children   - the masked value rendered in normal flow
 */
export const HoverReveal = ({ contractId, field, children, className = '' }) => {
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);
  const startedRef = useRef(false);

  const handleOpen = (open) => {
    if (!open || startedRef.current || !contractId) return;
    startedRef.current = true;
    setLoading(true);
    fetchSensitive(contractId)
      .then((d) => setRaw(getRawValue(d, field)))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip onOpenChange={handleOpen}>
        <TooltipTrigger asChild>
          <span
            className={`cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2 ${className}`}
            data-testid={`reveal-${field}-${contractId}`}
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-slate-900 text-white max-w-[320px]">
          <div className="flex items-center gap-2 text-xs">
            <Eye className="w-3 h-3 text-slate-300" />
            {loading ? (
              <span className="flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> a carregar...
              </span>
            ) : err ? (
              <span className="text-red-300">Sem permissão</span>
            ) : (
              <span className="font-mono">{raw || '—'}</span>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default HoverReveal;
